import React from "react";
import { Row, Col } from 'antd';
import { DescriptionText, H1Text } from "../../../../design";
import { AmountDivideByHundred } from "../../../../helper";
import { TAX_RATE_TYPES } from "../../../../appconstants";
import currencyFormatter from "../../../../utils/currencyformatter";

const SWIREPAY_PRIMARY = '#279dfe';
const SECTION_BG_COLOR = '#f9fafb';
const INPUT_BG_COLOR = '#f5f6fa';

type Props = {
  invoices: Array,
  currency: Object,
  invoice: Object,
}

const InvoiceLineItem = (props: Props) => {
  const { invoices, currency, invoice } = props;

  const getTotalAmount = () => {
    return invoice && invoice.total;
  };

  const getDiscountOff = (value) => {
    let discountOff;
    const months = value && value.validiyInMonths;
    if (months && value.percentageOff) {
      discountOff = `${value.percentageOff}% off for ${months} months`;
    } else if (!months && value.percentageOff) {
      discountOff = `${value.percentageOff}% off ${value.validity}`;
    } else if (months && value.amountOff) {
      discountOff = `${value.amountOff / 100} off for ${months} months`;
    } else if (!months && value.amountOff) {
      discountOff = `${value.amountOff / 100} off ${value.validity}`;
    }
    return discountOff;
  };

  const getAmountOff = (value) => {
    let amountOff;
    if (value && value.percentageOff) {
      const amount = AmountDivideByHundred(invoice && invoice.amount * value.percentageOff);
      amountOff = currency && currencyFormatter((AmountDivideByHundred(amount)), currency.name);
    } else if (value && value.amountOff) {
      amountOff = currency && currencyFormatter(AmountDivideByHundred(value.amountOff), currency.name);
    }
    return amountOff;
  };

  return (
    <div className="mt-4">
      <Row
        gutter={[16, 0]}
        style={{
          borderRadius: '12px',
          border: `1px solid ${INPUT_BG_COLOR}`,
          margin: 0,
          overflow: 'hidden',
        }}
      >
        {/* Header */}
        <Col span={24} className="p-0">
          <Row style={{ background: INPUT_BG_COLOR, padding: '10px 8px' }}>
            <Col span={12}>
              <DescriptionText className="m-0" fontWeight="600">Description</DescriptionText>
            </Col>
            <Col span={4}>
              <DescriptionText className="m-0" fontWeight="600">Rate</DescriptionText>
            </Col>
            <Col span={4}>
              <DescriptionText className="m-0" fontWeight="600">Quantity</DescriptionText>
            </Col>
            <Col span={4}>
              <DescriptionText className="m-0" fontWeight="600">Total</DescriptionText>
            </Col>
          </Row>
        </Col>

        {/* Line items */}
        <Col span={24} className="p-0">
          {invoices && invoices.map((value, index) => (
            <Row key={index} style={{ borderBottom: `1px solid ${INPUT_BG_COLOR}`, padding: '10px 8px' }}>
              <Col span={12}>
                <DescriptionText color="#1a1a1a" fontWeight="400" className="m-0">{value.name}</DescriptionText>
              </Col>
              <Col span={4}>
                <DescriptionText color="#000000" fontWeight="400" className="m-0">
                  {value.amount && currency && currencyFormatter(AmountDivideByHundred(value.amount), currency.name)}
                </DescriptionText>
              </Col>
              <Col span={4}>
                <DescriptionText color="#000000" fontWeight="400" className="m-0">{value.quantity}</DescriptionText>
              </Col>
              <Col span={4}>
                <DescriptionText color="#000000" fontWeight="400" className="m-0">
                  {value.quantity && value.amount && currency && currencyFormatter(AmountDivideByHundred(value.quantity * value.amount), currency.name)}
                </DescriptionText>
              </Col>
            </Row>
          ))}
        </Col>

        {/* Summary rows */}
        <Col span={24} className="p-0">
          {invoice && invoice.coupon && invoice.coupon.name && (
            <Row style={{ borderBottom: `1px solid ${INPUT_BG_COLOR}`, padding: '10px 8px', background: SECTION_BG_COLOR }}>
              <Col span={12}>
                <DescriptionText color="#000000" fontWeight="400" className="m-0">
                  {invoice.coupon.name}<br />
                  <span style={{ fontSize: '12px' }}>{getDiscountOff(invoice.coupon)}</span>
                </DescriptionText>
              </Col>
              <Col span={8} />
              <Col span={4}>
                <DescriptionText color="#000000" fontWeight="400" className="m-0">
                  ({getAmountOff(invoice.coupon)})
                </DescriptionText>
              </Col>
            </Row>
          )}
          {invoice?.subscriptionDiscountAmount && (
            <Row style={{ borderBottom: `1px solid ${INPUT_BG_COLOR}`, padding: '10px 8px', background: SECTION_BG_COLOR }}>
              <Col span={12}>
                <DescriptionText fontWeight="600" className="m-0">Subscription Discount</DescriptionText>
              </Col>
              <Col span={8} />
              <Col span={4}>
                <DescriptionText fontWeight="600" className="m-0">
                  ({currency && currencyFormatter(AmountDivideByHundred(invoice.subscriptionDiscountAmount), currency.name)})
                </DescriptionText>
              </Col>
            </Row>
          )}
          {invoice && invoice.taxAmount && invoice.taxAmount.map((value, index) => (
            <Row key={index} style={{ borderBottom: `1px solid ${INPUT_BG_COLOR}`, padding: '10px 8px', background: SECTION_BG_COLOR }}>
              <Col span={12}>
                <DescriptionText color="#000000" fontWeight="400" className="m-0">
                  {value.taxRates.displayName}<br />
                  <span style={{ fontSize: '12px' }}>{value.taxRates.inclusive ? TAX_RATE_TYPES.INCLUSIVE : TAX_RATE_TYPES.EXCLUSIVE}</span>
                </DescriptionText>
              </Col>
              <Col span={4}>
                <DescriptionText color="#000000" fontWeight="400" className="m-0">
                  {value.taxRates.percentage} %
                </DescriptionText>
              </Col>
              <Col span={4} />
              <Col span={4}>
                <DescriptionText color="#000000" fontWeight="400" className="m-0">
                  {currency && currencyFormatter(AmountDivideByHundred(value.tax), currency.name)}
                </DescriptionText>
              </Col>
            </Row>
          ))}
          {invoice && invoice.shippingAmount > 0 && (
            <Row style={{ borderBottom: `1px solid ${INPUT_BG_COLOR}`, padding: '10px 8px', background: SECTION_BG_COLOR }}>
              <Col span={12}>
                <DescriptionText color="#000000" fontWeight="400" className="m-0">Shipping Fee</DescriptionText>
              </Col>
              <Col span={8} />
              <Col span={4}>
                <DescriptionText color="#000000" fontWeight="400" className="m-0">
                  {currency && currencyFormatter(AmountDivideByHundred(invoice.shippingAmount), currency.name)}
                </DescriptionText>
              </Col>
            </Row>
          )}
          {invoice && invoice.surchargeAmount > 0 && (
            <Row style={{ borderBottom: `1px solid ${INPUT_BG_COLOR}`, padding: '10px 8px', background: SECTION_BG_COLOR }}>
              <Col span={12}>
                <DescriptionText color="#000000" fontWeight="400" className="m-0">Surcharge Amount</DescriptionText>
              </Col>
              <Col span={8} />
              <Col span={4}>
                <DescriptionText color="#000000" fontWeight="400" className="m-0">
                  {currency && currencyFormatter(AmountDivideByHundred(invoice.surchargeAmount), currency.name)}
                </DescriptionText>
              </Col>
            </Row>
          )}
          {invoice?.overdueFeeAmount && (
            <Row style={{ borderBottom: `1px solid ${INPUT_BG_COLOR}`, padding: '10px 8px', background: SECTION_BG_COLOR }}>
              <Col span={12}>
                <DescriptionText color="#000000" fontWeight="400" className="m-0">Overdue Fee Amount (+)</DescriptionText>
              </Col>
              <Col span={8} />
              <Col span={4}>
                <DescriptionText color="#000000" fontWeight="400" className="m-0">
                  {currency && currencyFormatter(AmountDivideByHundred(invoice.overdueFeeAmount), currency.name)}
                </DescriptionText>
              </Col>
            </Row>
          )}
          {invoice?.invoiceType === "PARTIAL" && invoice?.lateFeeAmount && (
            <Row style={{ borderBottom: `1px solid ${INPUT_BG_COLOR}`, padding: '10px 8px', background: SECTION_BG_COLOR }}>
              <Col span={12}>
                <DescriptionText color="#000000" fontWeight="400" className="m-0">Late Fee Amount (+)</DescriptionText>
              </Col>
              <Col span={8} />
              <Col span={4}>
                <DescriptionText color="#000000" fontWeight="400" className="m-0">
                  {currency && currencyFormatter(AmountDivideByHundred(invoice.lateFeeAmount), currency.name)}
                </DescriptionText>
              </Col>
            </Row>
          )}
          {invoice?.invoiceType === "PARTIAL" && invoice?.overdueInterestAmount && (
            <Row style={{ borderBottom: `1px solid ${INPUT_BG_COLOR}`, padding: '10px 8px', background: SECTION_BG_COLOR }}>
              <Col span={12}>
                <DescriptionText color="#000000" fontWeight="400" className="m-0">Overdue Interest Amount (+)</DescriptionText>
              </Col>
              <Col span={8} />
              <Col span={4}>
                <DescriptionText color="#000000" fontWeight="400" className="m-0">
                  {currency && currencyFormatter(AmountDivideByHundred(invoice.overdueInterestAmount), currency.name)}
                </DescriptionText>
              </Col>
            </Row>
          )}
        </Col>

        {/* Total row */}
        <Col span={24} className="p-0">
          <Row style={{ background: SWIREPAY_PRIMARY, padding: '14px 8px' }}>
            <Col span={12}>
              <H1Text fontWeight="600" color="#ffffff" fontSize="16px" className="m-0">Total Amount</H1Text>
            </Col>
            <Col span={8} />
            <Col span={4}>
              <H1Text fontWeight="600" color="#ffffff" fontSize="16px" className="m-0">
                {currency && currencyFormatter(AmountDivideByHundred(getTotalAmount()), currency.name)}
              </H1Text>
            </Col>
          </Row>
          {invoice?.invoiceType === "PARTIAL" && invoice?.paidAmount && (
            <Row style={{ borderBottom: `1px solid ${INPUT_BG_COLOR}`, padding: '10px 8px', background: SECTION_BG_COLOR }}>
              <Col span={12}>
                <DescriptionText fontWeight="600" className="m-0">Paid Amount (-)</DescriptionText>
              </Col>
              <Col span={8} />
              <Col span={4}>
                <DescriptionText fontWeight="600" className="m-0">
                  {currency && currencyFormatter(AmountDivideByHundred(invoice.paidAmount), currency.name)}
                </DescriptionText>
              </Col>
            </Row>
          )}
          {invoice?.invoiceType === "PARTIAL" && invoice?.paidAmount && (
            <Row style={{ padding: '10px 8px', background: SECTION_BG_COLOR }}>
              <Col span={12}>
                <DescriptionText fontWeight="600" className="m-0">Remaining Amount</DescriptionText>
              </Col>
              <Col span={8} />
              <Col span={4}>
                <DescriptionText fontWeight="600" className="m-0">
                  {currency && currencyFormatter(AmountDivideByHundred(getTotalAmount() - invoice.paidAmount), currency.name)}
                </DescriptionText>
              </Col>
            </Row>
          )}
        </Col>
      </Row>
    </div>
  );
};

export default InvoiceLineItem;
